import { appConfig, type AppConfig } from "../config";
import {
  getConsentState,
  subscribeToConsent,
  type ConsentState,
} from "../privacy/consent";
import type { AnalyticsEventName, PublicAnalyticsMetadata } from "./api";

const GA_SCRIPT_ID = "conan-ga4";
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const FORBIDDEN_KEY = /(answer|restriction|diet|food|health|goal|score|diagnos|profile|consideration|email|phone|whatsapp|name)/i;

const GA_EVENT_MAP: Record<AnalyticsEventName, string> = {
  FunnelPageView: "page_view",
  QuizStart: "quiz_start",
  QuestionView: "question_view",
  QuestionAnswered: "question_answered",
  Quiz25: "quiz_progress_25",
  Quiz50: "quiz_progress_50",
  Quiz75: "quiz_progress_75",
  QuizComplete: "quiz_complete",
  RecipePreviewView: "recipe_preview_view",
  RecipeOpen: "recipe_open",
  LeadFormView: "lead_form_view",
  Lead: "generate_lead",
  ResultView: "result_view",
  WhatsAppOptIn: "whatsapp_opt_in",
  OfferBridgeView: "offer_bridge_view",
  OfferView: "offer_view",
  OfferScroll50: "offer_scroll_50",
  OfferScroll90: "offer_scroll_90",
  OfferClick: "offer_click",
  MonthlyPlanClick: "monthly_plan_click",
  AnnualPlanClick: "annual_plan_click",
  InitiateCheckout: "begin_checkout",
  Purchase: "purchase",
  QuizAbandon: "quiz_abandon",
  ReturnSession: "return_session",
};

type GtagArguments = [command: string, target: string | Date, parameters?: Record<string, unknown>];
type Gtag = (...args: GtagArguments) => void;

interface GaWindow extends Window {
  dataLayer?: unknown[];
  gtag?: Gtag;
}

export type Ga4DeliveryStatus = "dispatched" | "skipped" | "blocked-consent" | "unconfigured" | "unavailable";

export interface Ga4TrackResult {
  provider: "ga4";
  status: Ga4DeliveryStatus;
  eventId: string;
  eventName: AnalyticsEventName;
  providerEventName: string;
}

export interface Ga4TrackOptions {
  eventId: string;
  metadata?: PublicAnalyticsMetadata;
  pagePath?: string;
  consent?: ConsentState;
}

export interface Ga4AdapterOptions {
  config?: AppConfig;
  window?: Window;
  document?: Document;
  loadTimeoutMs?: number;
}

const GA_METADATA_KEYS: Record<string, string> = {
  questionId: "question_id",
  step: "step",
  total: "total",
  device: "device",
  variant: "variant",
  channel: "channel",
  messageStep: "message_step",
  contentVersion: "content_version",
  checkoutStatus: "checkout_status",
  value: "value",
  valueMinor: "value",
  currency: "currency",
  reason: "reason",
};

export function sanitizeGa4Metadata(metadata: PublicAnalyticsMetadata | undefined): Record<string, string | number | boolean> {
  if (!metadata) return {};
  const output: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(metadata)) {
    if (FORBIDDEN_KEY.test(key)) continue;
    const mappedKey = GA_METADATA_KEYS[key];
    if (!mappedKey) continue;
    if (key === "valueMinor") {
      if (typeof value === "number" && Number.isInteger(value) && value >= 0) output.value = value / 100;
      continue;
    }
    if (key === "currency") {
      if (typeof value === "string" && /^[A-Za-z]{3}$/.test(value)) output.currency = value.toUpperCase();
      continue;
    }
    if (typeof value === "string") output[mappedKey] = value.slice(0, 100);
    if (typeof value === "number" && Number.isFinite(value)) output[mappedKey] = value;
    if (typeof value === "boolean") output[mappedKey] = value;
  }
  return output;
}

function createGtag(browser: GaWindow): Gtag {
  browser.dataLayer ??= [];
  if (browser.gtag) return browser.gtag;
  const gtag: Gtag = (...args) => {
    browser.dataLayer!.push(args);
  };
  browser.gtag = gtag;
  return gtag;
}

export class Ga4Adapter {
  private readonly config: AppConfig;
  private readonly browser: GaWindow | undefined;
  private readonly page: Document | undefined;
  private readonly loadTimeoutMs: number;
  private loadPromise: Promise<boolean> | undefined;
  private initializedMeasurementId: string | undefined;
  private consentRevision = 0;
  private observedConsentAllowed: boolean | undefined;

  constructor(options: Ga4AdapterOptions = {}) {
    this.config = options.config ?? appConfig;
    this.browser = options.window ?? (typeof window === "undefined" ? undefined : window);
    this.page = options.document ?? (typeof document === "undefined" ? undefined : document);
    this.loadTimeoutMs = options.loadTimeoutMs ?? 5_000;
  }

  applyConsent(state?: ConsentState): void {
    this.observedConsentAllowed = this.isAllowed(state);
    this.consentRevision += 1;
    const gtag = this.browser?.gtag;
    if (!gtag) return;
    gtag("consent", "update", {
      analytics_storage: this.observedConsentAllowed ? "granted" : "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
  }

  async track(eventName: AnalyticsEventName, options: Ga4TrackOptions): Promise<Ga4TrackResult> {
    if (!UUID_PATTERN.test(options.eventId)) throw new TypeError("GA4 eventId must be an RFC 4122 UUID v4");
    const providerEventName = GA_EVENT_MAP[eventName];
    if (!this.config.ga4MeasurementId) {
      return { provider: "ga4", status: "unconfigured", eventId: options.eventId, eventName, providerEventName };
    }
    if (!this.isAllowed(options.consent)) {
      return { provider: "ga4", status: "blocked-consent", eventId: options.eventId, eventName, providerEventName };
    }
    const consentRevision = this.consentRevision;
    if (!await this.load(options.pagePath)) {
      return { provider: "ga4", status: "unavailable", eventId: options.eventId, eventName, providerEventName };
    }
    if (this.consentRevision !== consentRevision && this.observedConsentAllowed === false) {
      return { provider: "ga4", status: "blocked-consent", eventId: options.eventId, eventName, providerEventName };
    }
    const gtag = this.browser?.gtag;
    if (!gtag) {
      return { provider: "ga4", status: "unavailable", eventId: options.eventId, eventName, providerEventName };
    }
    const metadata = sanitizeGa4Metadata(options.metadata);
    gtag("event", providerEventName, {
      ...metadata,
      event_id: options.eventId,
      ...(this.pageLocation(options.pagePath) ? { page_location: this.pageLocation(options.pagePath) } : {}),
      ...(this.config.trackingDebug ? { debug_mode: true } : {}),
    });
    if (this.config.trackingDebug) {
      console.debug("[tracking:ga4]", { eventName: providerEventName, eventId: options.eventId, keys: Object.keys(metadata) });
    }
    return { provider: "ga4", status: "dispatched", eventId: options.eventId, eventName, providerEventName };
  }

  private isAllowed(state?: ConsentState): boolean {
    const decision = state ?? getConsentState();
    return !this.config.analyticsConsentRequired || decision.analytics;
  }

  private pageLocation(pagePath?: string): string | undefined {
    if (!this.browser) return undefined;
    const candidate = pagePath?.startsWith("/") ? pagePath : this.browser.location.pathname;
    const path = candidate
      .replace(/^\/b\/quiz\/resultado\/[^/]+(?=\/|$)/, "/b/quiz/resultado/:token")
      .replace(/^\/quiz\/resultado\/[^/]+(?=\/|$)/, "/quiz/resultado/:token")
      .replace(/^\/oferta\/[^/]+(?=\/|$)/, "/oferta/:token");
    try {
      return new URL(path, this.browser.location.origin).toString();
    } catch {
      return undefined;
    }
  }

  private async load(pagePath?: string): Promise<boolean> {
    const measurementId = this.config.ga4MeasurementId;
    if (!measurementId || !this.browser || !this.page) return false;
    if (this.initializedMeasurementId === measurementId && this.browser.gtag) return true;
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = new Promise<boolean>((resolve) => {
      const gtag = createGtag(this.browser!);
      gtag("consent", "default", {
        analytics_storage: "denied",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
        wait_for_update: 500,
      });
      gtag("js", new Date());
      gtag("consent", "update", {
        analytics_storage: "granted",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
      });
      gtag("config", measurementId, {
        send_page_view: false,
        anonymize_ip: true,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
        ...(this.pageLocation(pagePath) ? { page_location: this.pageLocation(pagePath) } : {}),
      });
      this.initializedMeasurementId = measurementId;

      const existing = this.page!.getElementById(GA_SCRIPT_ID) as HTMLScriptElement | null;
      if (existing?.dataset.loaded === "true") {
        resolve(true);
        return;
      }
      const script = existing ?? this.page!.createElement("script");
      const timeout = globalThis.setTimeout(() => resolve(false), this.loadTimeoutMs);
      const finish = (loaded: boolean) => {
        globalThis.clearTimeout(timeout);
        if (loaded) script.dataset.loaded = "true";
        resolve(loaded);
      };
      script.addEventListener("load", () => finish(true), { once: true });
      script.addEventListener("error", () => finish(false), { once: true });
      if (!existing) {
        script.id = GA_SCRIPT_ID;
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
        this.page!.head.appendChild(script);
      }
    });
    const loaded = await this.loadPromise;
    if (!loaded) {
      this.loadPromise = undefined;
      this.initializedMeasurementId = undefined;
      this.page?.getElementById(GA_SCRIPT_ID)?.remove();
    }
    return loaded;
  }
}

export const ga4 = new Ga4Adapter();

subscribeToConsent((state) => ga4.applyConsent(state));

export function trackGa4Event(eventName: AnalyticsEventName, options: Ga4TrackOptions): Promise<Ga4TrackResult> {
  return ga4.track(eventName, options);
}
